/**
 * useLoanNavIcons — the "show icons in the drilldown tab strip" preference.
 *
 * Dropping the glyphs buys back ~20px per tab, which on a laptop is the
 * difference between the strip fitting and it scrolling — so this is a density
 * control, not decoration. Icons ON is the default; the choice is persisted
 * (partner-scoped) so it survives reloads and follows the user from loan to
 * loan.
 *
 * Same module-level pub/sub as `useLoanChatFabHidden`: the toggle lives in the
 * header's Settings menu and the strip it controls is a sibling subtree, and a
 * `storage` event only fires in OTHER tabs — so subscribers are notified
 * directly and the strip re-renders the moment the switch flips.
 *
 * Returns [showIcons, setShowIcons].
 */
import { useCallback, useEffect, useState } from "react";

import { partnerStorage } from "../../../utils/storage/partnerStorage";

const KEY = "loanDrilldown.navIcons";
const listeners = new Set();
let cached = null;

const read = () => {
  if (cached === null) {
    try {
      // Absent → on. Only an explicit "0" turns the icons off, so a user who
      // has never touched the toggle gets the icons.
      cached = partnerStorage.getItem(KEY) !== "0";
    } catch {
      cached = true;
    }
  }
  return cached;
};

const write = (show) => {
  cached = !!show;
  try {
    partnerStorage.setItem(KEY, cached ? "1" : "0");
  } catch {
    // ignore storage failures — the in-memory value still drives the UI
  }
  listeners.forEach((fn) => fn(cached));
};

export function useLoanNavIcons() {
  const [showIcons, setShowIconsState] = useState(read);

  useEffect(() => {
    const fn = (v) => setShowIconsState(v);
    listeners.add(fn);
    // Re-sync in case the value changed between render and subscribe.
    setShowIconsState(read());
    return () => listeners.delete(fn);
  }, []);

  const setShowIcons = useCallback((v) => write(v), []);
  return [showIcons, setShowIcons];
}

export default useLoanNavIcons;
